import React, { useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { useTranslation } from "react-i18next";
import GenericTable from "../../../components/GenericTable";
import { PAID_ADS_PROVIDER_CARDS } from "../../../constants/paidAdsCampaignCatalog";

const statusColor = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "published") return "success";
  if (s === "publishing") return "info";
  if (s === "failed") return "error";
  return "default";
};

const iconForProvider = (provider) => {
  const c = PAID_ADS_PROVIDER_CARDS.find((x) => x.id === provider);
  if (!c) return null;
  return <Box sx={{ color: c.color ?? "inherit", display: "flex", alignItems: "center" }}>{c.icon({})}</Box>;
};

export default function PaidAdsCampaignTable({
  rows = [],
  isLoading,
  search,
  onSearchChange,
  onEdit,
  onRefresh,
  connections = null,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
}) {
  const { t } = useTranslation("settings");

  const metaReady = Boolean(connections?.meta?.connected) && Boolean(connections?.meta?.adAccountId);
  const googleReady = Boolean(connections?.google?.connected) && Boolean(connections?.google?.customerId);

  const gridRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        id: row.id,
        providerLabel: row.provider === "google" ? "Google Ads" : "Meta Ads",
        statusLabel: t(`paidAdsPage.status_${row.status}`, { defaultValue: row.status }),
        externalIdDisplay: row.external_campaign_id || "—",
      })),
    [rows, t]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 88 },
      {
        field: "name",
        headerName: t("paidAdsPage.colCampaignName"),
        flex: 1.2,
        minWidth: 200,
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {iconForProvider(params.row.provider)}
            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }} title={params.value}>
              {params.value}
            </Box>
          </Stack>
        ),
      },
      {
        field: "providerLabel",
        headerName: t("paidAdsPage.colProvider"),
        flex: 0.8,
        minWidth: 130,
      },
      {
        field: "statusLabel",
        headerName: t("paidAdsPage.colStatus"),
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            color={statusColor(params.row.status)}
            label={params.value}
            variant="outlined"
          />
        ),
      },
      {
        field: "externalIdDisplay",
        headerName: t("paidAdsPage.colExternalId"),
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary" noWrap title={params.row.external_campaign_id || ""}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "actions",
        headerName: t("paidAdsPage.tableActions"),
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const full = rows.find((r) => Number(r.id) === Number(params.row.id)) || params.row;
          return (
            <Tooltip title={t("shared.edit")}>
              <IconButton size="small" onClick={() => onEdit?.(full)}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [rows, t, onEdit]
  );

  const rightActions =
    connections != null || typeof onRefresh === "function" ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
        {connections != null && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
              {t("paidAdsPage.workspaceIntegrationsLabel")}
            </Typography>
            <Chip
              size="small"
              color={metaReady ? "success" : "warning"}
              label={metaReady ? t("paidAdsPage.chipMetaReady") : t("paidAdsPage.chipMetaPending")}
              variant="outlined"
            />
            <Chip
              size="small"
              color={googleReady ? "success" : "warning"}
              label={googleReady ? t("paidAdsPage.chipGoogleReady") : t("paidAdsPage.chipGooglePending")}
              variant="outlined"
            />
          </>
        )}
        {typeof onRefresh === "function" && (
          <Button size="small" variant="outlined" onClick={onRefresh} sx={{ textTransform: "uppercase" }}>
            {t("paidAdsPage.refreshAll")}
          </Button>
        )}
      </Stack>
    ) : null;

  return (
    <GenericTable
      rows={gridRows}
      columns={columns}
      pageSize={pageSize}
      page={page}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      searchValue={search}
      onSearchChange={onSearchChange}
      isLoading={isLoading}
      paginationMode="client"
      checkboxSelection={false}
      rightActions={rightActions}
      onExportPDFTitle={t("paidAdsPage.exportTableTitle")}
      exportFileName="campanhas-pagas"
      onRowClick={(row) => {
        const full = rows.find((r) => Number(r.id) === Number(row.id));
        if (full) onEdit?.(full);
      }}
    />
  );
}
